import { API_BASE_URL } from '../../../lib/api';

import { apiFetch } from '@/lib/apiFetch';
import { withTenant } from '../../../lib/tenantHeaders';

/**
 * Catálogos que consume el formulario de Solicitudes LCL.
 *
 * Comparte con el aéreo los catálogos transversales (clientes,
 * asesores, incoterms, tipos de mercancía) porque son los mismos
 * maestros del operador; lo que cambia es la ruta: aquí se piden
 * PUERTOS (`/seaports`) en vez de aeropuertos.
 *
 * Los headers se construyen dentro de cada función (ver nota en
 * `lclRequestsService.js`).
 */
const jsonHeaders = () => withTenant({ 'Content-Type': 'application/json' });

/**
 * Devuelve los países que tienen al menos un puerto en la DB del
 * tenant, para que el dropdown de País Origen/Destino muestre solo
 * los países donde el operador tiene cobertura portuaria.
 *
 * Si el endpoint falla, devuelve lista vacía en vez de romper el
 * formulario (mismo criterio que el aéreo).
 */
export const getCountriesWithSeaports = async (q = '') => {
  try {
    const query = q ? `?q=${encodeURIComponent(q)}` : '';
    const res = await apiFetch(`${API_BASE_URL}/seaports/countries${query}`, {
      headers: withTenant(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json?.success && Array.isArray(json.data)) return json;
    throw new Error('Respuesta inesperada');
  } catch {
    return { success: true, data: [] };
  }
};

/**
 * Puertos filtrados por país (y opcionalmente estado).
 */
export const getSeaportsByPais = async (pais, estado = 'Activo') => {
  const url = `${API_BASE_URL}/seaports/by-pais?pais=${encodeURIComponent(
    pais || '',
  )}&estado=${encodeURIComponent(estado)}`;
  const res = await apiFetch(url, { headers: withTenant() });
  return res.json();
};

/**
 * Búsqueda de clientes activos por razón social o NIT.
 */
export const searchClients = async (q) => {
  const url = `${API_BASE_URL}/clients/search?q=${encodeURIComponent(q || '')}`;
  const res = await apiFetch(url, { headers: withTenant() });
  return res.json();
};

/**
 * Lista de usuarios asesores comerciales activos.
 */
export const getAsesores = async () => {
  const res = await apiFetch(`${API_BASE_URL}/users/asesores`, { headers: withTenant() });
  return res.json();
};

/**
 * Incoterms activos ordenados alfabéticamente (para combo del form).
 */
export const getActiveIncoterms = async () => {
  const res = await apiFetch(`${API_BASE_URL}/incoterms/filter`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      limit: 200,
      offset: 1,
      query: { estado: 'Activo' },
      sort: { codigo: 1 },
    }),
  });
  const json = await res.json();
  return json?.data || [];
};

/**
 * Tipos de mercancía activos (para combo de cada fila del detalle).
 */
export const getActiveCommodityTypes = async () => {
  const res = await apiFetch(`${API_BASE_URL}/commodity-types/filter`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      limit: 200,
      offset: 1,
      query: { estado: 'Activo' },
      sort: { nombre: 1 },
    }),
  });
  const json = await res.json();
  return json?.data || [];
};
